/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import PaymentErrorIcon from 'src/components/icons/PaymentErrorIcon'

interface PaymentErrorProps {
  title: string
  message: string
  onPrimaryAction: () => void
  buttonLabel: string
  /** Replaces the error glyph — a state that is not a failure (Sep 24: "Payment Received"). */
  icon?: React.ReactNode
  /** A second, filled action beside the first — e.g. Try Again on a retryable failure. */
  retryAction?: { label: string; onClick: () => void }
}

/**
 * Displays a centered error state for the registration payment flow — and, since
 * Sep 24, the states a payment attempt can be in instead of a card form (processing,
 * received, unresolved, in progress), which pass their own icon.
 */
const PaymentError: React.FC<PaymentErrorProps> = ({
  title,
  message,
  onPrimaryAction,
  buttonLabel,
  icon,
  retryAction,
}) => {
  return (
    <div
      css={css`
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 32px 16px;
      `}
    >
      <div
        css={css`
          max-width: 600px;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          text-align: center;
        `}
      >
        {icon ?? <PaymentErrorIcon />}
        <Text variant='heading1' weight='semiBold' color={colors.NEUTRAL_0}>
          {title}
        </Text>
        <Text
          variant='paragraphLarge'
          weight='regular'
          color={colors.NEUTRAL_0}
        >
          {message}
        </Text>
        <div
          css={css`
            width: 100%;
            display: flex;
            justify-content: center;
            gap: 8px;
          `}
        >
          <Button
            text={buttonLabel}
            onClick={onPrimaryAction}
            variant='secondary'
            style={css`
              flex: 0 0 auto;
              width: auto;
            `}
          />
          {retryAction ? (
            <Button
              text={retryAction.label}
              onClick={retryAction.onClick}
              variant='primary'
              style={css`
                flex: 0 0 auto;
                width: auto;
              `}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default PaymentError
